import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import type { Expo, ExpoSession, Release } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '../../ui/states';
import { ApprovalHandoff, AuditStatement, RefusalWithRemedy, StepUpPanel } from '../../ui/guards';
import { useSession } from '../../lib/auth/session';
import type { HandoffGrant, HandoffRequest } from '../../lib/auth/handoff';
import { statusTone } from './Expos';

const REASON_CODES = ['go_live', 'hotfix', 'rollback', 'compliance', 'operational'];

type Verb = 'activate' | 'rollback';

/** The permission each verb is step-up gated on, from `catalogue`'s own route table. */
const ACTION_OF: Record<Verb, string> = {
  activate: 'expo.release.activate',
  rollback: 'expo.release.rollback',
};

const TAKEDOWN_ACTION = 'content.moderate';

interface ValidationReport {
  status?: string;
  findings?: { projection?: string; path?: string; rule?: string; detail?: string }[];
}

/** `ADM-10` release builder · `ADM-11` activation and rollback. */
export function AdminReleases(): ReactElement {
  const { expoId = '' } = useParams();
  const { t, formatDateTime } = useI18n();
  const listPath = `/v1/admin/expos/${expoId}/releases`;
  const releases = useApiList<Release>(listPath);
  const sessions = useApiList<ExpoSession>(`/v1/admin/expos/${expoId}/sessions`);
  // EVERY approval on this screen binds to the EXPO's current version — the takedown's, the
  // activation's and the rollback's alike — because `cat_releases` carries no version an approval
  // could be pinned to and `expo_expos` does (`iam.ApprovalAggregateReleaseActivation`). So the
  // screen has to have read the expo before it can state any of the three acts.
  const expo = useApiResource<Expo>(expoId ? `/v1/admin/expos/${expoId}` : null);
  const build = useApiAction<Release>([listPath]);
  const [selected, setSelected] = useState<string | null>(null);

  const [sessionId, setSessionId] = useState('');
  const [channel, setChannel] = useState('public');
  const [tier, setTier] = useState('full');
  const [locales, setLocales] = useState('ar,en');

  return (
    <>
      <PageTitle title={t('admin.expo.releases')}>{t('admin.releases.desc')}</PageTitle>

      <Card title={t('admin.expo.releases')}>
        {releases.isLoading ? <LoadingState /> : null}
        {releases.error ? <ErrorState error={releases.error} onRetry={() => void releases.refetch()} /> : null}
        {releases.data ? (
          <DataTable<Release>
            caption={t('admin.expo.releases')}
            rows={releases.data.items ?? []}
            rowKey={(r) => r.release_id}
            empty={<EmptyState>{t('admin.releases.desc')}</EmptyState>}
            columns={[
              {
                key: 'id',
                header: t('table.identifier'),
                cell: (r) => (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => setSelected(r.release_id)}
                  >
                    <Ltr>{r.release_id.slice(0, 8)}</Ltr>
                  </button>
                ),
              },
              {
                key: 'status',
                header: t('table.status'),
                cell: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge>,
              },
              { key: 'tier', header: 'tier', cell: (r) => <Ltr>{r.tier}</Ltr> },
              { key: 'channel', header: 'channel', cell: (r) => <Ltr>{r.channel}</Ltr> },
              { key: 'hash', header: 'manifest_hash', cell: (r) => <Ltr>{(r.manifest_hash ?? '').slice(0, 12)}</Ltr> },
              { key: 'built', header: 'built_at', cell: (r) => formatDateTime(r.built_at) },
              { key: 'activated', header: 'activated_at', cell: (r) => formatDateTime(r.activated_at) },
            ]}
          />
        ) : null}
      </Card>

      <Card title="build">
        <AuditStatement>
          <p>
            <Ltr>expo.release.build</Ltr> — a build produces an immutable artifact; a failed build is
            not publishable and there is no runtime filter to fall back on.
          </p>
        </AuditStatement>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            build.mutate(newIntent({
              path: `${listPath}:build`,
              body: {
                expo_session_id: sessionId,
                channel,
                tier,
                locales: locales
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              },
            }));
          }}
        >
          <Select
            label="expo_session_id"
            name="expo_session_id"
            value={sessionId}
            options={[{ value: '', label: '—' }].concat(
              (sessions.data?.items ?? []).map((s) => ({
                value: s.expo_session_id,
                label: `${s.sequence} · ${s.label_en || s.label_ar}`,
              })),
            )}
            onChange={setSessionId}
          />
          {sessions.error ? <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} /> : null}
          <Field label="channel" name="channel" value={channel} onChange={setChannel} ltr />
          <Field label="tier" name="tier" value={tier} onChange={setTier} ltr />
          <Field label="locales" name="locales" value={locales} onChange={setLocales} ltr />
          {build.error ? <ErrorState error={build.error} /> : null}
          <button className="btn" type="submit" disabled={build.isPending}>
            build
          </button>
        </form>
      </Card>

      {expo.error ? <ErrorState error={expo.error} onRetry={() => void expo.refetch()} /> : null}
      {selected ? (
        <ReleaseDetail
          releaseId={selected}
          expoId={expoId}
          expoVersion={expo.data?.version ?? expo.data?.data.version ?? null}
          onExpoMoved={() => void expo.refetch()}
        />
      ) : null}
      <Takedowns expoId={expoId} expoVersion={expo.data?.version ?? expo.data?.data.version ?? null} />
    </>
  );
}

/**
 * `ADM-11`. Activation and rollback are two DIFFERENT acts, and this screen never confuses them.
 *
 * The previous version drove both through one mutation, offered the approver id as a text box, and
 * — when a rollback was refused — offered "retry" wired to `activate`. Each of the three is fixed
 * here: an intent is minted per act and carries its own step-up action, the approver comes out of
 * an approval a second principal recorded in their own session, and a refusal retries THAT intent
 * or nothing.
 */
function ReleaseDetail({
  releaseId,
  expoId,
  expoVersion,
  onExpoMoved,
}: {
  releaseId: string;
  expoId: string;
  expoVersion: number | null;
  onExpoMoved: () => void;
}): ReactElement {
  const { t } = useI18n();
  const path = `/v1/admin/releases/${releaseId}`;
  const release = useApiResource<Release & { version?: number }>(path);
  const validationResource = useApiResource<ValidationReport & { version?: number }>(`${path}/validation`);
  const act = useApiAction<Release>([path, `/v1/admin/expos/${expoId}/releases`]);
  const session = useSession();

  const [reason, setReason] = useState(REASON_CODES[0] ?? 'go_live');
  const [sessionId, setSessionId] = useState('');
  const [targetRelease, setTargetRelease] = useState('');
  const [verb, setVerb] = useState<Verb>('activate');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  /**
   * THE RELEASE'S ETAG IS NOT A NUMBER, AND IS NOT AN EXPO VERSION (round-2 B2).
   *
   * `catalogue.ReleaseETag` is `"<release_id>:<status>"` — "a release's bytes are immutable; what
   * moves is its STATE". Two different things used to be derived from it and both were wrong: the
   * transport parsed a leading integer out of the uuid and called it a version, and this screen
   * then handed that number to the approval as the EXPO's `expected_version`. The two are now
   * separate and each comes from where it actually lives — the tag is echoed verbatim in
   * `If-Match`, and the approval's version is the expo's, read by the parent screen.
   */
  const etag = release.data?.etag ?? null;
  const action = ACTION_OF[verb];
  const aggregateType = verb === 'activate' ? 'release_activation' : 'release_rollback';

  /**
   * The approval is bound to the EXPO at its current version AND to WHICH release is published.
   *
   * `iam.approvalProposed` requires a digest for both release aggregates: "without it the approver
   * is shown 'expo E at version 7' and authorises every validated release in the hall, in every
   * channel, locale and tier". `POST /v1/approvals` renders that digest from the release row
   * itself, so the request carries the one field the server reads — `release.release_id`, which for
   * a rollback is the release being ROLLED BACK TO, since that is the artifact the public ends up
   * seeing.
   */
  const approvedRelease = verb === 'activate' ? releaseId : targetRelease;
  const handoff: HandoffRequest | null =
    expoVersion === null || approvedRelease === ''
      ? null
      : {
          action,
          aggregateType,
          aggregateId: expoId,
          expectedVersion: expoVersion,
          release: { release_id: approvedRelease },
          reasonCode: reason,
          label: `${verb} release ${approvedRelease}`,
        };

  const stepUp = session.grantFor(action);
  const ready = etag !== null && grant !== null && (verb === 'activate' ? sessionId !== '' : targetRelease !== '');

  const run = (): void => {
    if (!etag || !grant) return;
    // THERE IS NO `approver_user_id` (P4-l). `releaseApproval`'s decoder is strict and would refuse
    // the field outright; the second actor is the approver of the `iam_approvals` row this
    // transition consumes inside its own transaction, and the server takes them from that row.
    const body: Record<string, unknown> = { reason_code: reason };
    if (verb === 'activate') body['expo_session_id'] = sessionId;
    else body['target_release_id'] = targetRelease;
    act.mutate(newIntent({ path: `${path}:${verb}`, body, ifMatch: etag, action, label: `${verb} ${releaseId}` }));
  };

  const findings = validationResource.data?.data.findings ?? [];

  return (
    <>
      <Card title={`${t('admin.expo.releases')} · ${releaseId.slice(0, 8)}`}>
        {release.isLoading ? <LoadingState /> : null}
        {release.error ? <ErrorState error={release.error} /> : null}
        {release.data ? (
          <dl className="kv">
            <dt>release_id</dt>
            <dd>
              <Ltr>{release.data.data.release_id}</Ltr>
            </dd>
            <dt>{t('table.status')}</dt>
            <dd>{release.data.data.status}</dd>
            <dt>manifest_hash</dt>
            <dd>
              <Ltr>{release.data.data.manifest_hash}</Ltr>
            </dd>
            <dt>ETag</dt>
            <dd>
              <Ltr>{etag ?? '—'}</Ltr>
            </dd>
          </dl>
        ) : null}
      </Card>

      <Card title="validation">
        {validationResource.isLoading ? <LoadingState /> : null}
        {validationResource.error ? <ErrorState error={validationResource.error} /> : null}
        {validationResource.data ? (
          findings.length === 0 ? (
            <EmptyState title="no findings" />
          ) : (
            <DataTable<{ projection?: string; path?: string; rule?: string; detail?: string }>
              caption="validation"
              rows={findings}
              rowKey={(f) => `${f.projection ?? ''}|${f.path ?? ''}|${f.rule ?? ''}`}
              columns={[
                { key: 'projection', header: 'projection', cell: (f) => <Ltr>{f.projection ?? ''}</Ltr> },
                { key: 'path', header: 'path', cell: (f) => <Ltr>{f.path ?? ''}</Ltr> },
                { key: 'rule', header: 'rule', cell: (f) => <Ltr>{f.rule ?? ''}</Ltr> },
                { key: 'detail', header: 'detail', cell: (f) => f.detail ?? '' },
              ]}
            />
          )
        ) : null}
      </Card>

      <Card title={t('admin.releases.activate.desc')}>
        <AuditStatement>
          <p>
            <Ltr>expo.release.activate</Ltr> / <Ltr>expo.release.rollback</Ltr> — step-up AND four
            eyes. The pointer update and the outbox event happen in one transaction.
          </p>
        </AuditStatement>
        {/* ONE act is chosen first, and everything below binds to it: the step-up, the approval and
            the request that is finally sent all name the same verb. */}
        <Select
          label={t('admin.expo.transition')}
          name="release-verb"
          value={verb}
          options={[
            { value: 'activate', label: 'activate' },
            { value: 'rollback', label: 'rollback' },
          ]}
          onChange={(v) => {
            setVerb(v as Verb);
            setGrant(null);
          }}
        />
        <Select
          label={t('guard.reason')}
          name="release-reason"
          value={reason}
          options={REASON_CODES.map((c) => ({ value: c, label: c }))}
          onChange={setReason}
        />
        {verb === 'activate' ? (
          <Field label="expo_session_id" name="release-session" value={sessionId} onChange={setSessionId} ltr required />
        ) : (
          <Field
            label="target_release_id"
            name="target_release_id"
            value={targetRelease}
            onChange={setTargetRelease}
            ltr
            required
            hint="rollback reactivates a previous COMPLETE release; a hot fix is a new complete release, never an edit."
          />
        )}

        {/* The step-up is raised for exactly this action and is spent by exactly this request. */}
        {stepUp ? null : <StepUpPanel action={action} />}
        <ApprovalHandoff request={handoff} onGranted={setGrant} />

        {act.error ? (
          <RefusalWithRemedy
            error={act.error}
            action={action}
            retryExact={act.retryLast}
            onReread={() => {
              // BOTH READS ARE STALE AFTER A CAS LOSS, and they are stale for different reasons:
              // the release's ETag fences the transition, the expo's version binds the approval.
              // Re-reading one and keeping the other produced a fresh grant bound to a version
              // that had already moved.
              setGrant(null);
              void release.refetch();
              onExpoMoved();
            }}
          />
        ) : null}
        <div className="row">
          <button className="btn" type="button" disabled={act.isPending || !ready} onClick={run}>
            {verb}
          </button>
        </div>
      </Card>
    </>
  );
}

/**
 * `content.moderate` — terminal for this expo, and therefore guarded three ways at once: a step-up
 * raised for this action, an approval a second principal recorded in their own session, and a typed
 * confirmation that is checked INSIDE the request path rather than by a disabled button.
 */
function Takedowns({ expoId, expoVersion }: { expoId: string; expoVersion: number | null }): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const takedown = useApiAction(['/v1/admin/takedowns']);
  const [subjectType, setSubjectType] = useState('participation');
  const [subjectId, setSubjectId] = useState('');
  const [legalRef, setLegalRef] = useState('');
  const [reason, setReason] = useState('compliance');
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  /**
   * The aggregate is `takedown`, NOT `expo` (P4-k).
   *
   * They are separate aggregates precisely so the digest can be REQUIRED: "an `expo` approval is a
   * verb applied to a version (go-live) and carries none". The proposal is what the approver is
   * actually deciding about — which content is removed, and under what reason — and
   * `catalogue.TakedownDigest` re-renders exactly these three fields plus the expo when the removal
   * spends the approval, so an approval for one removal cannot be spent on another.
   */
  const handoff: HandoffRequest | null =
    expoVersion === null || subjectId === ''
      ? null
      : {
          action: TAKEDOWN_ACTION,
          aggregateType: 'takedown',
          aggregateId: expoId,
          expectedVersion: expoVersion,
          takedown: { subject_type: subjectType, subject_id: subjectId, reason_code: reason },
          reasonCode: reason,
          label: `take down ${subjectType} ${subjectId}`,
        };

  const stepUp = session.grantFor(TAKEDOWN_ACTION);

  const run = (): void => {
    if (!grant) return;
    takedown.mutate(newIntent({
      path: '/v1/admin/takedowns',
      action: TAKEDOWN_ACTION,
      label: `take down ${subjectId}`,
      // The typed confirmation is part of the intent, so the execution path refuses a request whose
      // confirmation does not match — a disabled button is a hint, not a guard.
      confirm: { expected: subjectId, typed },
      // NO `approver_user_id` (P4-k): `handleTakedown` decodes strictly and would refuse it, and
      // `catalogue.Takedown` takes the approver out of the `iam_approvals` row it consumes.
      body: {
        subject_type: subjectType,
        subject_id: subjectId,
        expo_id: expoId,
        legal_reference: legalRef,
        reason_code: reason,
        note,
      },
    }));
  };

  return (
    <Card title={t('admin.takedown.title')} description={t('admin.takedown.desc')}>
      <AuditStatement>
        <p>
          <Ltr>content.moderate</Ltr> — step-up and four eyes, with a reason code and a legal
          reference. Terminal for this expo.
        </p>
      </AuditStatement>
      <Select
        label="subject_type"
        name="subject_type"
        value={subjectType}
        options={['participation', 'project', 'offer', 'unit'].map((s) => ({ value: s, label: s }))}
        onChange={setSubjectType}
      />
      <Field label="subject_id" name="subject_id" value={subjectId} onChange={setSubjectId} ltr required />
      <Field label="legal_reference" name="legal_ref" value={legalRef} onChange={setLegalRef} ltr required />
      <Field label="reason_code" name="takedown_reason" value={reason} onChange={setReason} ltr required />
      <TextArea label={t('guard.note')} name="takedown_note" value={note} onChange={setNote} />
      <Field
        label={t('guard.typed.label', { value: subjectId || '—' })}
        name="takedown_typed"
        value={typed}
        onChange={setTyped}
        ltr
        {...(typed && typed !== subjectId ? { error: t('guard.typed.mismatch') } : {})}
      />
      {stepUp ? null : <StepUpPanel action={TAKEDOWN_ACTION} />}
      <ApprovalHandoff request={handoff} onGranted={setGrant} />
      {takedown.error ? (
        <RefusalWithRemedy error={takedown.error} action={TAKEDOWN_ACTION} retryExact={takedown.retryLast} />
      ) : null}
      <button
        className="btn btn--destructive"
        type="button"
        disabled={takedown.isPending || subjectId === '' || typed !== subjectId || !grant}
        onClick={run}
      >
        take down
      </button>
    </Card>
  );
}
